import { useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import CsvDownload from 'react-json-to-csv';

import cx from 'classnames/bind';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getTagFrameDetails } from 'modules/api/content';

import eyeSvg from 'assets/icons/eye_empty.svg';
import tagSvg from 'assets/icons/tag.svg';
import IconButton from 'lib/IconButton';
import Modal, { ModalSize } from 'lib/Modal';
import { transformTagFrameDetails } from 'utils/apiAdapters';
import Table from 'lib/Table/Table';
import { TableHeight } from 'lib/Table/Table.types';
import { FrameTagDetails } from 'components/common/types/FrameTag.types';
import usePendoTracking from 'customHooks/usePendoTracking';

type ViewRendererProps = {
  data: {
    id: string;
    frameCount: number;
    name: string;
    group: {
      name: string;
    };
  };
};

const ViewRenderer: React.FC<ViewRendererProps> = ({ data }) => {
  const { getPendoDataId } = usePendoTracking();
  const [showDialog, setShowDialog] = useState(false);
  const [frames, setFrames] = useState<FrameTagDetails[]>([]);

  const columnDefs = [
    {
      headerName: 'Frame code',
      field: 'code',
    },
    {
      headerName: 'Postcode',
      field: 'postCode',
    },
    {
      headerName: 'Coordinates',
      cellRenderer: (params: ICellRendererParams) => `${params.data.latitude} Lat </br>${params.data.longitude} Lon`,
    },
    {
      headerName: 'Product format',
      field: 'productFormat',
      cellRenderer: (params: ICellRendererParams) => `${params.value.name}`,
    },
    {
      headerName: 'Environment',
      field: 'environment',
      resizable: false,
    },
  ];

  const openDialog = async (): Promise<void> => {
    try {
      const framesData = await getTagFrameDetails(cancelFunctions, data.id);

      setFrames(framesData);
      setShowDialog(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const closeDialog = (): void => {
    setShowDialog(false);
  };

  const isDisabled = !data.frameCount;

  return (
    <div className="flex items-center">
      <IconButton
        handleOnClick={openDialog}
        icon={eyeSvg}
        name="Open frame details"
        dataPendoId={getPendoDataId("Frame Tags - See Linked Frames 'eye' icon")}
      />
      <span
        className={cx('ml-1 align-middle sub-header-base', {
          'text-pinkRed-500 opacity-50': isDisabled,
          'text-sky': !isDisabled,
        })}
      >
        {data.frameCount}
      </span>
      <Modal
        isOpen={showDialog}
        icon={tagSvg}
        title={`${data.group.name} ${data.name} Tag (${data.frameCount})`}
        onClose={closeDialog}
        size={ModalSize.LARGE}
        actionButtons={
          <CsvDownload
            filename="frames-details.csv"
            data={transformTagFrameDetails(frames)}
            className="py-2.5 px-3.5 bg-primary text-neutral-50 sub-header-base rounded-md"
          >
            <p>Export list</p>
          </CsvDownload>
        }
      >
        <div className="flex justify-between flex-col h-full">
          <Table
            dataTestId="frame-tags-table"
            columnDefs={columnDefs}
            rowData={frames}
            tableHeight={TableHeight.NORMAL}
            extraGridOptions={{
              enableCellTextSelection: true,
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ViewRenderer;
